import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { logoBackPath } from "../config";
import { NameContext } from "../context/NameContext";
import { useOrbitMenuConfig } from "../hooks/useOrbitMenuConfig";
import { mainMenuItems, aboutSubmenuItems } from "../config/orbitMenuConfig";

const BitterButtonWithMenu = React.forwardRef(({ onMenuClick, onOverlayOpen }, ref) => {
  const containerRef = React.useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("main");
  const ticking = React.useRef(false);

  const navigate = useNavigate();
  const { name } = useContext(NameContext);

  // Use OrbitMenu configuration
  const { config } = useOrbitMenuConfig();

  const BOTTOM_BUFFER_CLOSED = 170; // Space from bottom when menu is closed
  const BOTTOM_BUFFER_OPEN = 300; // Space from bottom when menu is open (adjust based on menu size)
  const TOOLTIP_DURATION = 6000; // 6 seconds
  const SMOOTH_SCROLL = true; // ON/OFF switch for smooth scrolling

  useEffect(() => {
    // Check if tooltip was already shown (using localStorage for persistence)
    const hasSeenTooltip = localStorage.getItem('menuTooltipShown') === 'true';

    const updatePosition = () => {
      if (!containerRef.current) return;

      const currentScroll = window.scrollY;
      const viewportHeight = window.innerHeight;
      const initialTopOffset = 112; // mt-28 = 7rem = 112px
      const bottomBuffer = isMenuOpen ? BOTTOM_BUFFER_OPEN : BOTTOM_BUFFER_CLOSED;

      // Calculate maximum translateY - ensures button never goes below virtual bottom
      const maxTranslateY = Math.max(0, viewportHeight - initialTopOffset - bottomBuffer);

      // Clamp translateY to valid range
      const clampedTranslateY = Math.min(Math.max(0, currentScroll), maxTranslateY);

      // Apply transform - single positioning mode
      containerRef.current.style.transform = `translateY(${clampedTranslateY}px)`;
      containerRef.current.style.top = `${initialTopOffset}px`;
      containerRef.current.style.bottom = 'auto';

      // Show tooltip after user scrolls a bit (100px), only once ever, and only when menu is closed
      if (currentScroll > 100 && !hasScrolled && !hasSeenTooltip && !isMenuOpen) {
        setHasScrolled(true);
        setShowTooltip(true);

        // Mark as shown in localStorage
        localStorage.setItem('menuTooltipShown', 'true');

        // Hide after TOOLTIP_DURATION
        setTimeout(() => {
          setShowTooltip(false);
        }, TOOLTIP_DURATION);
      }

      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updatePosition);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled, isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      setCurrentMenu("main");
    }
  }, [isMenuOpen]);

  const handleClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Build menu items with dynamic actions
  const buildMenuItems = (items) => {
    return items.map(item => ({
      ...item,
      label: item.dynamic && item.label.includes('{name}')
        ? item.label.replace('{name}', name || 'Demo')
        : item.label,
      onClick: () => {
        // Handle different action types
        if (item.action === 'openOverlay') {
          setIsMenuOpen(false);
          if (onOverlayOpen) onOverlayOpen();
        } else if (item.action === 'openSubmenu') {
          setCurrentMenu(item.submenu);
        } else if (item.action === 'closeSubmenu') {
          setCurrentMenu('main');
        } else if (item.route) {
          navigate(item.route);
          setIsMenuOpen(false);
        }
      }
    }));
  };

  const processedMainMenu = buildMenuItems(mainMenuItems);
  const processedAboutMenu = buildMenuItems(aboutSubmenuItems);

  const radius = config.visual.radius;
  const buttonSize = config.visual.button.width;
  const startAngle = config.visual.startAngle || 0;
  const currentMenuItems = currentMenu === "main" ? processedMainMenu : processedAboutMenu;

  const getPosition = (angle) => {
    const rad = ((angle + startAngle) - 90) * (Math.PI / 180);
    return {
      x: Math.cos(rad) * radius,
      y: Math.sin(rad) * radius
    };
  };

  return (
    <>
      <motion.div
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className="fixed right-4 z-[100]"
      >
        {/* Tooltip */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{
            opacity: showTooltip ? 1 : 0,
            x: showTooltip ? 0 : 20
          }}
          transition={{ duration: 0.3 }}
          className="absolute right-16 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ display: showTooltip ? 'block' : 'none' }}
        >
          <div className="bg-gray-900 text-orange-50 text-xs font-mono px-3 py-2 rounded-lg shadow-lg whitespace-nowrap relative">
            Menü öffnen
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900"></div>
          </div>
        </motion.div>

        <motion.div
          className="relative flex items-center justify-center cursor-pointer rounded-full bg-gray-50 border border-gray-200 shadow-lg hover:shadow-gray-500/75"
          style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
          whileHover={{ scale: 1.1 }}
          onClick={handleClick}
        >
          <div
            className="flex items-center justify-center rounded-full overflow-hidden"
            style={{ width: `${buttonSize - 8}px`, height: `${buttonSize - 8}px` }}
          >
            <img
              src={logoBackPath}
              alt="Denis Bitter - Button"
              className="w-15 h-15 rounded-full"
              loading="lazy"
            />
          </div>
        </motion.div>

        {/* Radial Menu Items - attached to button */}
        <AnimatePresence>
          {isMenuOpen && (
            <div className="absolute top-0 left-0">
              {currentMenuItems.map((item, index) => {
                const position = getPosition(item.angle);

                return (
                  <motion.button
                    key={`${currentMenu}-${item.label}`}
                    initial={{
                      scale: 0,
                      x: 0,
                      y: 0,
                      opacity: 0
                    }}
                    animate={{
                      scale: 1,
                      x: position.x,
                      y: position.y,
                      opacity: 1
                    }}
                    exit={{
                      scale: 0,
                      x: 0,
                      y: 0,
                      opacity: 0
                    }}
                    transition={{
                      type: "spring",
                      stiffness: config.animation.menuItem.stiffness,
                      damping: config.animation.menuItem.damping,
                      delay: index * config.animation.menuItem.staggerDelay
                    }}
                    onClick={item.onClick}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`absolute rounded-full flex flex-col items-center justify-center cursor-pointer shadow-lg transform ${
                      item.isMainMenu
                        ? "bg-gray-900 text-orange-50 border-2 border-orange-400"
                        : item.isBack
                        ? "bg-orange-600 text-orange-50 border-2 border-orange-400"
                        : "bg-gray-800 text-orange-50 border border-gray-600"
                    } hover:bg-gray-700 transition-colors`}
                    style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
                  >
                    <span className="text-[10px] font-mono text-center leading-tight px-1">
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Backdrop when menu is open */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: config.animation.backdrop.duration }}
            className="fixed inset-0 z-[99]"
            style={{
              backgroundColor: config.visual.colors.backdrop,
              backdropFilter: `blur(${config.visual.backdrop.blur})`,
              WebkitBackdropFilter: `blur(${config.visual.backdrop.blur})`
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
});

BitterButtonWithMenu.displayName = 'BitterButtonWithMenu';

export default BitterButtonWithMenu;
